import { Center, Spinner, Text } from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import type { AttachmentFragment } from "@src/__generated__/urql-graphql";
import type { AttachmentStorage } from "@src/components/ui-kit/TextEditor/extensions/AttachmentNode";
import type { Attachment } from "@src/components/ui-kit/TextEditor/extensions/UploadAttachmentPlugin";
import { AttachmentView } from "@src/components/widgets/Modals/ModalCommunication/components/Attachments/TaskAttachment";
import { useStore } from "@src/utils/hooks";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { type FunctionComponent, useEffect, useState } from "react";

// TODO: add attachment prop types to NodeViewProps somehow to have it more type-safe
export const AttachmentNodeView: FunctionComponent<NodeViewProps> = (props) => {
  const { taskDetailModalStore } = useStore();
  const { id, loading } = props.node.attrs;
  const [file, setFile] = useState<Attachment | undefined>(undefined);

  useEffect(() => {
    if (id && typeof id?.then === "function") {
      Promise.resolve<AttachmentFragment | undefined>(id)
        .then((file) => {
          // Check if the node still exists in the document
          const pos = props.getPos();
          if (
            typeof pos !== "number" ||
            pos < 0 ||
            pos >= props.editor.state.doc.content.size
          ) {
            return; // Node no longer exists or position is invalid
          }

          if (file) {
            const storage = props.editor.storage
              .attachment as AttachmentStorage;
            storage.attachments.push(
              taskDetailModalStore.createAttachmentFromFile(file),
            );

            try {
              props.editor.view.dispatch(
                props.editor.view.state.tr.setNodeMarkup(pos, undefined, {
                  id: file.public_id,
                  loading: false,
                }),
              );
            } catch (error) {
              console.error("Failed to update attachment node:", error);
            }
          } else {
            try {
              props.editor.view.dispatch(
                props.editor.view.state.tr.setNodeMarkup(pos, undefined, {
                  loading: false,
                  id: null,
                }),
              );
            } catch (error) {
              console.error("Failed to update attachment node:", error);
            }
          }
        })
        .catch((error) => {
          console.error("Upload failed:", error);
          // Handle upload failure by marking the node as failed
          const pos = props.getPos();
          if (
            typeof pos === "number" &&
            pos >= 0 &&
            pos < props.editor.state.doc.content.size
          ) {
            try {
              props.editor.view.dispatch(
                props.editor.view.state.tr.setNodeMarkup(pos, undefined, {
                  loading: false,
                  id: null,
                }),
              );
            } catch (error) {
              console.error("Failed to update failed attachment node:", error);
            }
          }
        });
    }
  }, [id]);

  useEffect(() => {
    const storage = props.editor.storage.attachment as AttachmentStorage;
    setFile(
      storage.attachments?.find(
        (attachment: Attachment) => attachment.id === id,
      ),
    );
  }, [props]);

  if (loading) {
    return (
      <NodeViewWrapper>
        <Center w="200px" h="100px" bg="grey.50" rounded="md">
          <Spinner size="lg" />
        </Center>
      </NodeViewWrapper>
    );
  }

  if (!loading && !file) {
    return (
      <NodeViewWrapper>
        <Center w="200px" h="100px" bg="grey.50" rounded="md">
          <Text fontWeight="bold">
            <Trans>Error</Trans>
          </Text>
        </Center>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      {file && (
        <AttachmentView
          mimeType={file.mime_type}
          id={id}
          name={file.filename}
          urls={{
            viewable: file.viewable,
            original: file.original,
            thumbnail: file.thumbnail,
          }}
          removeDisabled={!props.editor.isEditable}
          customRemove={() => props.deleteNode()}
        />
      )}
    </NodeViewWrapper>
  );
};
